import {
  GRAIN, PLANT_HOSTS, SPAN, isOrdinary, lookOf, newStrainMix,
  type StrainHost, type StrainTraits,
} from '@/world/strain';
import { effectiveTraitsAt, newTiltMix, type StrainField } from '@/world/strainField';
import type { Region } from '@/world/region';
import type { StrainReads } from '@/world/strainReads';
import {
  clampPheno, type PlantPhenotype, type PlantPhenotypeSource, type WildlifePhenotype,
} from '@/render/phenotype';

/**
 * STRAINS → PHENOTYPE — the adapter B14 was built toward, wired the day B1
 * woke (2026-08-19). The ground's effective mixture (baseline + tilt, the
 * same composition the ecology multiplies) becomes the visual axes the
 * renderer already knows how to draw, so a valley a god has pressed for a
 * season LOOKS like what it has become — and looks like what it EATS like,
 * because both read one mixture.
 *
 * The mapping is deliberately restrained, and derived rather than invented:
 *
 *  · **Pigment comes from the registry's own look.** `lookOf` has always
 *    derived a tint from the traits (never an independent roll); this
 *    projects that tint onto the phenotype's `light` and `warmth` axes, so
 *    the direction a strain shifts a stand's colour is the direction the
 *    card's look language was designed with. The tint is linear in the trait
 *    deviations, so blending traits first and tinting once equals tinting
 *    each member and blending — one call.
 *  · **Structure comes from yield.** What a stand puts into the world is the
 *    axis with an honest visible correlate: a rich strain's stand is denser
 *    and a little taller, a poor one sparser and shorter. Half the natural
 *    range at full deviation — a strain reads as a strain, not as a
 *    different species (identity survives, the foundation's own law).
 *  · **Nothing else.** Tolerance and exposure show through pigment alone
 *    (their tints are the strong ones), `spread` shows through NOTHING —
 *    the invisible axis stays invisible in the phenotype exactly as it does
 *    in the range and the card.
 *
 * Everything here is a pure read (law 9): same field state, same answer, and
 * a chunk shows the mixture as of its own rebuild — the same staleness
 * contract the env stamps have always had, refreshed by the same dirty-chunk
 * invalidation the presses and burns already emit.
 */

/** Tint units per 1.0 of `light` / `warmth` — calibrated to the TINT tables'
 *  own reach so a full-deviation strain lands well inside the natural ±1. */
const LIGHT_PER_TINT = 1 / 24;
const WARMTH_PER_TINT = 1 / 64;
/** How much foliage density / stature a full yield deviation buys. */
const DENSITY_PER_YIELD = 0.8;
const STATURE_PER_YIELD = 0.35;
/** Animal structure per yield deviation: worth reads as body. */
const MASS_PER_YIELD = 0.55;
const ANIMAL_STATURE_PER_YIELD = 0.3;

const scratchTraits: StrainTraits = { yield: 1, tolerance: 1, exposure: 1, spread: 1 };
const scratchMix = newStrainMix(GRAIN.mixMax + 1);
const scratchTilts = newTiltMix();

/** The shared trait→axes projection. `host` picks the tint's wardrobe rules. */
function axesOf(traits: StrainTraits, host: StrainHost): {
  light: number; warmth: number; dev: number;
} {
  const look = lookOf(traits, host);
  const [r, g, b] = look.tint;
  return {
    light: clampPheno((0.2126 * r + 0.7152 * g + 0.0722 * b) * LIGHT_PER_TINT),
    warmth: clampPheno((r - b) * WARMTH_PER_TINT),
    dev: (traits.yield - 1) / SPAN.yield,
  };
}

/**
 * A `PlantPhenotypeSource` over the live strain state. The threshold keeps
 * ordinary ground EXACTLY neutral — a blended deviation under half a percent
 * is quantisation, not biology — so the common case returns null and costs
 * the chunk path nothing at all.
 */
const PLANT_HOST_SET: ReadonlySet<string> = new Set<string>(PLANT_HOSTS);

export function strainPlantSource(
  region: Region, field: StrainField, now: () => number,
): PlantPhenotypeSource {
  return (kind, x, z) => {
    // the sixteen un-hosted kinds have no strains and pay nothing here —
    // a mixture evaluated for a kind the registry refuses would read as
    // all-ordinary anyway, at twenty-five hashes per plant
    if (!PLANT_HOST_SET.has(kind)) return null;
    effectiveTraitsAt(region, field, kind as StrainHost, x, z, now(),
      scratchTraits, scratchMix, scratchTilts);
    const t = scratchTraits;
    if (Math.abs(t.yield - 1) < 0.005 && Math.abs(t.tolerance - 1) < 0.005
      && Math.abs(t.exposure - 1) < 0.005) return null;
    const a = axesOf(t, kind as StrainHost);
    const out: Partial<PlantPhenotype> = {};
    if (a.light !== 0) out.light = a.light;
    if (a.warmth !== 0) out.warmth = a.warmth;
    if (a.dev !== 0) {
      out.foliageDensity = clampPheno(a.dev * DENSITY_PER_YIELD);
      out.stature = clampPheno(a.dev * STATURE_PER_YIELD);
    }
    return out;
  };
}

/**
 * The herd's phenotype off its cell's effective animal strain — `bodyAt`'s
 * render input, at the herd grain the ledger itself lives at. Undefined for
 * ordinary ground (the free path), for the unhosted species, and for a test's
 * hand-built view.
 */
export function strainHerdPhenotype(
  reads: StrainReads, species: string, sx: number, sz: number, now: number,
): Partial<WildlifePhenotype> | undefined {
  if (species !== 'grazer' && species !== 'browser'
    && species !== 'small_game' && species !== 'predator') return undefined;
  reads.hostTraitsAtCell(species, sx, sz, now, scratchTraits);
  const t = scratchTraits;
  if (Math.abs(t.yield - 1) < 0.005 && Math.abs(t.tolerance - 1) < 0.005
    && Math.abs(t.exposure - 1) < 0.005) return undefined;
  const a = axesOf(t, species);
  const out: Partial<WildlifePhenotype> = {};
  if (a.light !== 0) out.light = a.light;
  if (a.warmth !== 0) out.warmth = a.warmth;
  if (a.dev !== 0) {
    out.bodyMass = clampPheno(a.dev * MASS_PER_YIELD);
    out.stature = clampPheno(a.dev * ANIMAL_STATURE_PER_YIELD);
  }
  return out;
}

/**
 * What is biologically unusual about the ground under one plant, for the
 * card — the dominant non-ordinary member of its kind's mixture here, with
 * its trait deviations, or null on ordinary ground. Pure read.
 */
export function strainAbout(
  region: Region, field: StrainField, kind: string, x: number, z: number, nowS: number,
): { share: number; dy: number; dt: number; de: number } | null {
  if (!PLANT_HOST_SET.has(kind)) return null;
  effectiveTraitsAt(region, field, kind as StrainHost, x, z, nowS,
    scratchTraits, scratchMix, scratchTilts);
  // the mixture scratch still holds the effective mixture; find the leading
  // remarkable member and how much of this ground it is
  let share = 0;
  for (let i = 0; i < scratchMix.n; i++) {
    if (!isOrdinary(scratchMix.key[i])) { share = scratchMix.share[i]; break; }
  }
  if (share < 0.2) return null;
  const t = scratchTraits;
  const dy = (t.yield - 1) / SPAN.yield;
  const dt = (t.tolerance - 1) / SPAN.tolerance;
  const de = (t.exposure - 1) / SPAN.exposure;
  if (Math.abs(dy) < 0.05 && Math.abs(dt) < 0.05 && Math.abs(de) < 0.05) return null;
  return { share, dy, dt, de };
}
